import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { AlertTriangle, CheckCircle2, Info, Lock, OctagonAlert, SearchX, X } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Portal } from '../lib/overlay.js';
import { uiStrings } from '../strings.js';
const TONE_TEXT = {
    info: 'text-info',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
};
const TONE_SURFACE = {
    info: 'bg-info-subtle border-info/30',
    success: 'bg-success-subtle border-success/30',
    warning: 'bg-warning-subtle border-warning/30',
    danger: 'bg-danger-subtle border-danger/30',
};
const TONE_ICON = {
    info: Info,
    success: CheckCircle2,
    warning: AlertTriangle,
    danger: OctagonAlert,
};
/** Döner gösterge. `decorative` ise yalnız görseldir (yanındaki metin durumu söyler). */
export function Spinner({ size = 'md', label = uiStrings.loading, decorative = false, className, }) {
    const px = size === 'sm' ? 14 : 18;
    const svg = (_jsxs("svg", { width: px, height: px, viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", className: cn('motion-safe:animate-spin', className), children: [_jsx("circle", { cx: "12", cy: "12", r: "9", stroke: "currentColor", strokeOpacity: "0.25", strokeWidth: "3" }), _jsx("path", { d: "M21 12a9 9 0 0 0-9-9", stroke: "currentColor", strokeWidth: "3", strokeLinecap: "round" })] }));
    if (decorative)
        return svg;
    return (_jsxs("span", { role: "status", className: "inline-flex items-center gap-2 text-fg-muted", children: [svg, _jsx("span", { className: "sr-only", children: label })] }));
}
/**
 * Yarım daire gösterge (v3.5; referans panelin oran göstergelerinden uyarlandı). YALNIZ gerçek oran için (ör. plan kullanımı:
 * kullanılan / üst sınır). `max` 0 ya da bilinmiyorsa çağıran göstergeyi çizmez. Erişilebilir ad + `aria-valuetext`.
 */
/**
 * v3.6 oran metni kuralı (kullanım / tavan): gerçek sıfır "%0"; sıfırdan büyük ama %1'in altı "<%1" (kullanım varken %0
 * yazılmaz); tavanın altında tam sayıya AŞAĞI yuvarlanır (tavana ulaşmadan "%100" yazılmaz); tam tavanda "%100"; aşımda
 * gerçek oran (yuvarlama 100'e düşürürse ">%100"). Tavan bilinmiyor, sınırsız ya da sıfırsa oran YOKTUR (`null`) — sahte
 * oran üretilmez; çağıran ham değeri ve durumu yazar.
 */
export function ratioPercentLabel(value, max) {
    if (max === null || max === undefined || !Number.isFinite(max) || max <= 0)
        return null;
    if (!Number.isFinite(value) || value <= 0)
        return '%0';
    const p = (value / max) * 100;
    if (p < 1)
        return '<%1';
    if (value < max)
        return `%${String(Math.min(99, Math.floor(p)))}`;
    if (value === max)
        return '%100';
    const over = Math.floor(p);
    return over <= 100 ? '>%100' : `%${String(over)}`;
}
export function RadialMeter({ value, max, label, valueText, tone = 'primary', size = 160, className, percent, percentLabel, }) {
    const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
    const shown = percentLabel ??
        (percent === undefined ? (ratioPercentLabel(value, max) ?? '') : `%${String(percent)}`);
    const r = 42;
    const len = Math.PI * r;
    const stroke = {
        primary: 'stroke-primary',
        info: 'stroke-info',
        success: 'stroke-success',
        warning: 'stroke-warning',
        danger: 'stroke-danger',
    };
    return (_jsxs("div", { role: "meter", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": max, "aria-valuenow": Math.min(value, max), "aria-valuetext": valueText, className: cn('inline-flex flex-col items-center', className), style: { width: size }, children: [_jsxs("svg", { viewBox: "0 0 100 56", "aria-hidden": "true", className: "w-full", children: [_jsx("path", { d: "M8 50 A42 42 0 0 1 92 50", fill: "none", strokeWidth: "10", strokeLinecap: "round", className: "stroke-border" }), ratio > 0 ? (_jsx("path", { d: "M8 50 A42 42 0 0 1 92 50", fill: "none", strokeWidth: "10", strokeLinecap: "round", className: cn(stroke[tone], 'motion-safe:animate-dash-in'), strokeDasharray: `${String(len * ratio)} ${String(len)}`, "data-testid": "radial-meter-fill" })) : null] }), _jsx("span", { className: "-mt-7 text-lg font-semibold tabular-nums text-fg", children: shown }), _jsx("span", { className: "text-sm text-fg-muted", children: valueText })] }));
}
/** Belirli ilerleme (0–100). Belirsiz süreçlerde Spinner kullanılır. */
export function ProgressBar({ value, label, className, tone = 'primary', valueText, }) {
    const v = Math.max(0, Math.min(100, Math.round(value)));
    // Sıfırdan büyük ama yuvarlamada sıfıra düşen değer çubukta görünmez kalmasın (en az ince bir dilim)
    const fill = value > 0 ? Math.max(1, Math.min(100, value)) : 0;
    const fillTone = { primary: 'bg-primary', warning: 'bg-warning', danger: 'bg-danger' }[tone];
    return (_jsx("div", { role: "progressbar", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": v, "aria-valuetext": valueText, className: cn('h-1.5 w-full overflow-hidden rounded-full bg-surface-subtle', className), children: _jsx("div", { className: cn('h-full origin-left transition-transform duration-base motion-safe:animate-grow-x', fillTone), style: { transform: `scaleX(${fill / 100})` } }) }));
}
/** İçerik yüklenirken yer tutucu (yalnız görsel; durum metni ayrıca verilir). */
export function Skeleton({ className }) {
    return (_jsx("div", { "aria-hidden": "true", className: cn('rounded-md bg-surface-subtle motion-safe:animate-pulse', className) }));
}
/** Sayfa/bölüm düzeyi bildirim. `announce` açılışta ekran okuyucuya duyurur (hata: alert, diğerleri: status). */
export function Alert({ tone = 'info', title, children, action, onDismiss, announce = false, className, }) {
    const Icon = TONE_ICON[tone];
    const role = announce ? (tone === 'danger' ? 'alert' : 'status') : undefined;
    return (_jsxs("div", { role: role, className: cn('flex gap-3 rounded-card border px-4 py-3 text-base', TONE_SURFACE[tone], className), children: [_jsx(Icon, { size: 18, "aria-hidden": "true", className: cn('mt-0.5 shrink-0', TONE_TEXT[tone]) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "font-medium text-fg", children: title }), children ? _jsx("div", { className: "mt-0.5 text-fg-muted", children: children }) : null, action ? _jsx("div", { className: "mt-2", children: action }) : null] }), onDismiss ? (_jsx("button", { type: "button", onClick: onDismiss, "aria-label": uiStrings.dismissNotice, className: "-mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-fg-muted hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus", children: _jsx(X, { size: 16, "aria-hidden": "true" }) })) : null] }));
}
/** Alan altındaki hata metni; alan `aria-describedby` ile bu kimliğe bağlanır (Field bunu kendisi yapar). */
export function InlineError({ id, children }) {
    return (_jsxs("p", { id: id, className: "flex items-start gap-1.5 text-sm text-danger", children: [_jsx(OctagonAlert, { size: 14, "aria-hidden": "true", className: "mt-[3px] shrink-0" }), _jsx("span", { children: children })] }));
}
// ---------------------------------------------------------------------------
// Durum yüzeyleri: ikon + kısa metin + tek anlamlı eylem (karar paketi §5)
// ---------------------------------------------------------------------------
function StateSurface({ icon, tone, title, description, action, headingLevel = 2, className, testId, }) {
    const H = headingLevel === 2 ? 'h2' : 'h3';
    return (_jsxs("div", { className: cn('flex flex-col items-center gap-3 px-4 py-10 text-center sm:py-12', className), "data-testid": testId, children: [_jsx("span", { "aria-hidden": "true", className: cn('inline-flex h-12 w-12 items-center justify-center rounded-full', tone === 'danger' ? 'bg-danger-subtle text-danger' : 'bg-primary-subtle text-primary'), children: icon }), _jsxs("div", { className: "max-w-prose space-y-1", children: [_jsx(H, { className: "text-md font-semibold text-fg", children: title }), description ? _jsx("div", { className: "text-base text-fg-muted", children: description }) : null] }), action ? _jsx("div", { children: action }) : null] }));
}
/** Veri yokken: ne olduğunu ve sıradaki anlamlı eylemi söyler (yalnız dekoratif mesaj değil). */
export function EmptyState({ icon, ...p }) {
    return _jsx(StateSurface, { tone: "neutral", icon: icon ?? _jsx(SearchX, { size: 18 }), ...p });
}
/** Hata: ne olduğunu kullanıcı diliyle ve yapılabilir sonraki adımla söyler; teknik ayrıntı göstermez. */
export function ErrorState({ icon, ...p }) {
    return (_jsx("div", { role: "alert", children: _jsx(StateSurface, { tone: "danger", icon: icon ?? _jsx(OctagonAlert, { size: 18 }), ...p }) }));
}
/** Yetki yok: güvenli ve açık; kaynağın varlığı hakkında bilgi sızdırmaz. */
export function UnauthorizedState({ icon, ...p }) {
    return _jsx(StateSurface, { tone: "neutral", icon: icon ?? _jsx(Lock, { size: 18 }), ...p });
}
const ToastContext = createContext(null);
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error('useToast yalnız ToastProvider içinde kullanılır');
    return ctx;
}
export function ToastProvider({ children, regionLabel = uiStrings.notifications, durationMs = 6000, }) {
    const [items, setItems] = useState([]);
    const seq = useRef(1);
    const push = useCallback((t) => {
        const id = seq.current++;
        setItems((xs) => [
            ...xs.slice(-3),
            {
                id,
                tone: t.tone ?? 'success',
                title: t.title,
                description: t.description,
                testId: t.testId,
            },
        ]);
    }, []);
    const remove = useCallback((id) => {
        setItems((xs) => xs.filter((x) => x.id !== id));
    }, []);
    const value = useMemo(() => push, [push]);
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, _jsx(Portal, { live: true, children: _jsx("section", { "aria-label": regionLabel, className: "pointer-events-none fixed inset-x-4 bottom-4 z-toast flex flex-col items-stretch gap-2 sm:left-auto sm:right-4 sm:w-96", children: items.map((t) => (_jsx(ToastView, { item: t, durationMs: durationMs, onClose: remove }, t.id))) }) })] }));
}
function ToastView({ item, durationMs, onClose, }) {
    const [paused, setPaused] = useState(false);
    const persistent = item.tone === 'danger';
    useEffect(() => {
        if (persistent || paused)
            return;
        const t = window.setTimeout(() => {
            onClose(item.id);
        }, durationMs);
        return () => {
            window.clearTimeout(t);
        };
    }, [persistent, paused, durationMs, item.id, onClose]);
    const Icon = TONE_ICON[item.tone];
    return (_jsxs("div", { role: item.tone === 'danger' ? 'alert' : 'status', "data-testid": item.testId, onMouseEnter: () => {
            setPaused(true);
        }, onMouseLeave: () => {
            setPaused(false);
        }, onFocus: () => {
            setPaused(true);
        }, onBlur: () => {
            setPaused(false);
        }, 
        // v3.9: kısa yükselerek giriş (kaydetme / başarı / hata geri bildirimi fark edilsin; hareket azaltılmışsa yok)
        className: "pointer-events-auto flex gap-3 rounded-card border border-border bg-surface-raised px-4 py-3 shadow-overlay motion-safe:animate-rise-in", children: [_jsx(Icon, { size: 18, "aria-hidden": "true", className: cn('mt-0.5 shrink-0', TONE_TEXT[item.tone]) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "text-base font-medium text-fg", children: item.title }), item.description ? _jsx("p", { className: "text-sm text-fg-muted", children: item.description }) : null] }), _jsx("button", { type: "button", onClick: () => {
                    onClose(item.id);
                }, "aria-label": uiStrings.dismissNotice, className: "-mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-fg-muted hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus", children: _jsx(X, { size: 16, "aria-hidden": "true" }) })] }));
}
//# sourceMappingURL=feedback.js.map